/**
 * Reading one history sample back as a sentence: what was wrong, and how wrong.
 *
 * The timeline shows the same sample twice at two depths - a hover layer that has room
 * for one line, and a dialog that has room for all of it. Both read from here rather
 * than each picking the payload apart, so the summary under the pointer can never
 * disagree with the detail behind the click.
 *
 * Every field it reads is optional: samples recorded before the backend carried a
 * reason, and healthy samples of any age, simply produce an empty description.
 */

import type { HistorySample } from '../api/types'

export interface SampleCheck {
  name: string
  /** What the check itself wrote, or null when only its pass/fail was published. */
  detail: string | null
  latencyMs: number | null
}

export interface SampleDescription {
  /** Checks reporting down, important-looking names first is not knowable here, so A-Z. */
  failing: SampleCheck[]
  /** Entries from the payload's `issues` map that no failing check already covers. */
  issues: Array<{ name: string; detail: string }>
  /** The transport error - for an unreachable target, the whole story. */
  error: string | null
  /** True when there is anything at all to show beyond status and timing. */
  hasReason: boolean
  /**
   * One line for the hover layer: the error, or the failing checks by name.
   *
   * Names rather than details, deliberately. A detail string is a sentence and the
   * hover layer is a strip two lines tall; "database, redis" tells the reader which
   * incident this is, and the dialog tells them the rest.
   */
  headline: string | null
}

/** Checks whose names a failing-check entry already accounts for. */
function coveredBy(failing: SampleCheck[]): Set<string> {
  return new Set(failing.map((check) => check.name))
}

export function describeSample(sample: HistorySample): SampleDescription {
  const failing: SampleCheck[] = Object.entries(sample.ok ?? {})
    .filter(([, ok]) => ok === false)
    .map(([name]) => ({
      name,
      detail: sample.why?.[name] ?? null,
      latencyMs: sample.svc?.[name] ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const covered = coveredBy(failing)
  const issues = Object.entries(sample.iss ?? {})
    .filter(([name]) => !covered.has(name))
    .map(([name, detail]) => ({ name, detail }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const error = sample.err ?? null
  const hasReason = failing.length > 0 || issues.length > 0 || error !== null

  return { failing, issues, error, hasReason, headline: headlineFor(failing, issues, error) }
}

function headlineFor(
  failing: SampleCheck[],
  issues: Array<{ name: string }>,
  error: string | null,
): string | null {
  // An unreachable target has no checks to name - the error is the only thing that
  // happened, so it leads even when something else is also present.
  if (error) return error
  if (failing.length > 0) {
    const names = failing.map((check) => check.name)
    const shown = names.slice(0, 3).join(', ')
    const rest = names.length - 3
    const label = names.length === 1 ? 'check failing' : 'checks failing'
    return `${names.length} ${label}: ${shown}${rest > 0 ? ` +${rest} more` : ''}`
  }
  if (issues.length > 0) {
    return `${issues.length} issue${issues.length === 1 ? '' : 's'} reported: ` +
      issues.map((issue) => issue.name).slice(0, 3).join(', ')
  }
  return null
}
